import { ButtonVariantType, ColorType } from '@hts/shared/types';
import { removeComponentProps } from '@hts/shared/utils';
import { ButtonHTMLAttributes, memo, useMemo } from 'react';
import styles from '../styles/HtsButtons.module.scss';
import { HtsTypography } from './HtsTypography';

interface IHtsButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: ButtonVariantType;
  fullWidth?: boolean;
  underlineLabel?: boolean;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  captionContent?: React.ReactNode;
  color?: ColorType;
  typographyProps?: React.ComponentProps<typeof HtsTypography>;
}

export const HtsButton: React.FC<IHtsButtonProps> = memo(
  ({
    variant = 'contained',
    leftContent,
    rightContent,
    captionContent,
    label,
    fullWidth,
    className,
    underlineLabel,
    style,
    color,
    typographyProps,
    type = 'button',
    ...buttonProps
  }) => {
    const newTypographyProps = removeComponentProps(typographyProps, ['variant', 'style']);

    const variantStyles = useMemo(
      () => ({
        contained: styles.containedContainer,
        outlined: styles.outlinedContainer,
        cancel: styles.cancelContainer,
        remove: styles.removeContainer,
        text: styles.textContainer,
      }),
      []
    );

    const containerStyles: React.CSSProperties = useMemo(
      () => ({
        ...style,
        width: fullWidth ? '100%' : style?.width || 'max-content',
        paddingInline: variant === 'text' ? 0 : fullWidth ? 0 : style?.paddingInline || 27,
      }),
      [fullWidth, style, variant]
    );

    return (
      <button
        type={type}
        style={containerStyles}
        className={[styles.container, className, variantStyles[variant]].join(' ')}
        {...buttonProps}
      >
        {leftContent}

        <HtsTypography
          {...newTypographyProps}
          variant={typographyProps?.variant ?? 'button'}
          style={{
            ...typographyProps?.style,
            textDecoration: underlineLabel ? 'underline' : 'none',
          }}
          color={color}
          className={styles.text}
        >
          {label}
        </HtsTypography>

        {captionContent}

        {rightContent}
      </button>
    );
  }
);
